import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, ChevronRight } from "lucide-react";
import { useApi } from "@/lib/api";
import { personAvatarClass } from "./directory-data";
import { usePeople } from "./use-directory";

type TaggedRef = { id: number; name: string };

type OperationsTask = {
  id: number;
  status: string;
  people: TaggedRef[];
};

type Counts = Record<number, { open: number; done: number }>;

export function PeoplePage() {
  const api = useApi();
  const peopleQ = usePeople();
  const tasksQ = useQuery({
    queryKey: ["operations_tasks"],
    queryFn: async (): Promise<OperationsTask[]> => {
      const res = await api(`/api/operations_tasks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as OperationsTask[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const isLoading = peopleQ.isLoading || tasksQ.isLoading;

  const counts: Counts = {};
  for (const p of peopleQ.data ?? []) counts[p.id] = { open: 0, done: 0 };
  for (const row of tasksQ.data ?? []) {
    for (const p of row.people ?? []) {
      if (!counts[p.id]) counts[p.id] = { open: 0, done: 0 };
      if (row.status === "done") counts[p.id].done += 1;
      else counts[p.id].open += 1;
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b">
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-blue-500" />
          <div>
            <h1 className="text-xl font-semibold text-foreground">People</h1>
            <p className="text-sm text-muted-foreground">
              Open the page for any teammate to see action items tagged for them.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {(peopleQ.data ?? []).map((person) => {
            const c = counts[person.id] ?? { open: 0, done: 0 };
            const avatarColor = personAvatarClass(person.name, person.avatar_color);
            return (
              <Link
                key={person.id}
                to={`/people/${encodeURIComponent(person.name)}`}
                className="group rounded-xl border bg-card p-5 hover:border-foreground/20 hover:shadow-sm transition-all flex flex-col items-center gap-3 relative"
              >
                {c.open > 0 && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    {c.open}
                  </span>
                )}
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-white font-semibold text-lg ${avatarColor}`}
                >
                  {person.name[0]?.toUpperCase() ?? ""}
                </div>
                <div className="text-center">
                  <div className="text-sm font-semibold text-foreground">{person.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {isLoading ? "…" : `${c.open} open · ${c.done} done`}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

(PeoplePage as unknown as { path: string }).path = "/people";
